import { useState, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toasterApi } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../clients/testerClient';
import type { MemoryRecordResponse } from '@terra/shared-contracts-memory';

type UseUserPreferencesParams = {
  applicationId: string | undefined;
  enabled?: boolean;
  entryPoint?: 'torch_preference' | 'torch_chat_preference';
};

type UseUserPreferencesReturn = {
  preferences: MemoryRecordResponse[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createPreference: (content: string) => Promise<boolean>;
  deletePreference: (recordId: string) => Promise<boolean>;
  updatePreference: (recordId: string, content: string) => Promise<boolean>;
  isActionInProgress: (recordId: string) => boolean;
};

export function useUserPreferences({
  applicationId,
  enabled = true,
  entryPoint = 'torch_preference',
}: UseUserPreferencesParams): UseUserPreferencesReturn {
  const t = useTranslations('memories.preferences.toast');
  const queryClient = useQueryClient();
  const isCreatingRef = useRef(false);
  const actionInProgressRef = useRef<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const queryResult = useQuery({
    queryKey: ['user-preferences', applicationId],
    queryFn: async () => {
      if (!applicationId) return { records: [], totalCount: 0 };

      return terraProxyApiClient.listUserPreferenceRecords(applicationId);
    },
    enabled: enabled && !!applicationId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const refetch = async () => {
    await queryResult.refetch();
  };

  const invalidateAndRefetch = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['user-preferences', applicationId],
    });
    await queryClient.refetchQueries({
      queryKey: ['user-preferences', applicationId],
    });
  }, [applicationId, queryClient]);

  const createPreference = useCallback(
    async (content: string): Promise<boolean> => {
      if (!applicationId) {
        toasterApi.error({
          title: t('createErrorTitle'),
          description: t('createErrorDescription'),
        });

        return false;
      }

      if (!content.trim() || isCreatingRef.current) return false;

      isCreatingRef.current = true;

      try {
        await terraProxyApiClient.createUserPreferenceRecord(
          applicationId,
          content,
          entryPoint,
        );

        toasterApi.success({
          title: t('createSuccessTitle'),
          description: t('createSuccessDescription'),
        });

        await invalidateAndRefetch();

        return true;
      } catch (error) {
        console.error('Failed to save user preference:', error);
        toasterApi.error({
          title: t('createErrorTitle'),
          description:
            error instanceof Error
              ? error.message
              : t('createErrorDescription'),
        });

        return false;
      } finally {
        isCreatingRef.current = false;
      }
    },
    [applicationId, entryPoint, invalidateAndRefetch, t],
  );

  const deletePreference = useCallback(
    async (recordId: string): Promise<boolean> => {
      if (!applicationId || actionInProgressRef.current) return false;

      actionInProgressRef.current = recordId;
      setActionInProgress(recordId);

      try {
        await terraProxyApiClient.deleteUserPreferenceRecord(
          applicationId,
          recordId,
        );

        toasterApi.success({
          title: t('deleteSuccessTitle'),
          description: t('deleteSuccessDescription'),
        });

        await invalidateAndRefetch();

        return true;
      } catch (error) {
        console.error('Failed to delete user preference:', error);
        toasterApi.error({
          title: t('deleteErrorTitle'),
          description:
            error instanceof Error
              ? error.message
              : t('deleteErrorDescription'),
        });

        return false;
      } finally {
        actionInProgressRef.current = null;
        setActionInProgress(null);
      }
    },
    [applicationId, invalidateAndRefetch, t],
  );

  const updatePreference = useCallback(
    async (recordId: string, content: string): Promise<boolean> => {
      if (!applicationId || !content.trim() || actionInProgressRef.current)
        return false;

      actionInProgressRef.current = recordId;
      setActionInProgress(recordId);

      try {
        await terraProxyApiClient.updateUserPreferenceRecord(
          applicationId,
          recordId,
          content,
        );

        toasterApi.success({
          title: t('updateSuccessTitle'),
          description: t('updateSuccessDescription'),
        });

        await invalidateAndRefetch();

        return true;
      } catch (error) {
        console.error('Failed to update user preference:', error);
        toasterApi.error({
          title: t('updateErrorTitle'),
          description:
            error instanceof Error
              ? error.message
              : t('updateErrorDescription'),
        });

        return false;
      } finally {
        actionInProgressRef.current = null;
        setActionInProgress(null);
      }
    },
    [applicationId, invalidateAndRefetch, t],
  );

  const isActionInProgress = useCallback(
    (recordId: string) => actionInProgress === recordId,
    [actionInProgress],
  );

  const records = queryResult.data?.records ?? [];

  return {
    preferences: records,
    totalCount: records.length,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    refetch,
    createPreference,
    deletePreference,
    updatePreference,
    isActionInProgress,
  };
}
